import React from 'react';
import { ArrowRight, ShoppingBag, Phone, HeartHandshake, Coins } from 'lucide-react';
import ImageWithFallback from './ImageWithFallback';
import { trackEvent } from '../utils/analytics';
import { STORE_INFO } from '../constants';

interface HeroProps {
  onNavigate: (page: string) => void;
  onShop: (formType: 'inquiry' | 'sell') => void;
}

const Hero: React.FC<HeroProps> = ({ onNavigate, onShop }) => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const heroImageSrc = isMobile
    ? "https://images.unsplash.com/photo-1568667256549-094345857637?q=60&w=800&auto=format&fit=crop"
    : "https://images.unsplash.com/photo-1568667256549-094345857637?q=60&w=1600&auto=format&fit=crop";

  return (
    <section id="hero" className="relative min-h-[85vh] bg-brand-dark text-white overflow-hidden flex flex-col items-center justify-start pt-32 md:justify-center md:pt-0">
      {/* Background Image Container */}
      <div className="absolute inset-0 z-0">
        <ImageWithFallback 
          src={heroImageSrc}
          storageKey="hero_main"
          fallbackSrc="https://images.unsplash.com/photo-1568667256549-094345857637?q=60&w=1600&auto=format&fit=crop"
          alt="Borden's BookShoppe Interior" 
          className="w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-dark/95 via-brand-dark/60 to-brand-dark/95"></div>
      </div>
      
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-20 flex flex-col items-center text-center">
        <div className="animate-fade-in opacity-0" style={{ animationDelay: '0.1s', animationFillMode: 'forwards' }}>
          <h1 className="hidden md:block text-4xl md:text-7xl lg:text-8xl font-serif font-bold tracking-tight mb-4 drop-shadow-2xl leading-tight">
            Turn the Page.<br />
            <span className="text-brand-primary">Press Start.</span>
          </h1>
          
          <p className="hidden md:block text-base md:text-2xl text-stone-200 font-light drop-shadow-lg mb-10 max-w-3xl mx-auto leading-relaxed">
            Astoria's premier brick & mortar destination for Used Books, Video Games, Consoles, Accessories, DVDs, and CDs.
          </p>
        </div>
        
        <nav className="flex flex-wrap justify-center items-center gap-x-2 gap-y-2 mb-10 text-xs md:text-sm font-bold text-stone-300 uppercase tracking-[0.2em] animate-fade-in opacity-0" style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}>
          <button onClick={() => onNavigate('books')} className="hover:text-brand-primary transition-colors p-2">Books</button>
          <span className="text-brand-primary/40 select-none">•</span>
          <button onClick={() => onNavigate('games')} className="hover:text-brand-primary transition-colors p-2">Games & Consoles</button>
          <span className="text-brand-primary/40 select-none">•</span>
          <button onClick={() => onNavigate('movies')} className="hover:text-brand-primary transition-colors p-2">DVDs</button>
          <span className="text-brand-primary/40 select-none">•</span>
          <button onClick={() => onNavigate('music')} className="hover:text-brand-primary transition-colors p-2">CDs</button>
          <span className="text-brand-primary/40 select-none">•</span>
          <button onClick={() => onNavigate('services')} className="hover:text-brand-primary transition-colors p-2">Repair</button>
        </nav>

        <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto px-4 animate-fade-in opacity-0" style={{ animationDelay: '0.5s', animationFillMode: 'forwards' }}>
          <button 
            onClick={() => onNavigate('shop')}
            className="group inline-flex items-center justify-center px-8 py-5 text-sm md:text-lg font-bold rounded-full text-brand-dark bg-brand-primary hover:bg-white transition-all duration-300 shadow-[0_0_30px_rgba(217,119,6,0.3)]"
          >
            <ShoppingBag className="w-5 h-5 mr-2" />
            Shop Online
          </button>
          
          <button 
            onClick={() => onNavigate('visit')} 
            className="group inline-flex items-center justify-center px-8 py-5 border-2 border-white/30 backdrop-blur-sm text-sm md:text-lg font-bold rounded-full text-white hover:bg-white hover:text-brand-dark transition-all duration-300"
          >
            Visit Us <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>

          <a 
            href={`tel:${STORE_INFO.phone}`}
            className="md:hidden group inline-flex items-center justify-center px-8 py-5 border-2 border-white/30 backdrop-blur-sm text-sm font-bold rounded-full text-white hover:bg-white hover:text-brand-dark transition-all duration-300"
          >
            <Phone className="w-5 h-5 mr-2" />
            Call Us
          </a>
        </div>

        {/* Mobile Quick Links */}
        <div className="md:hidden mt-8 grid grid-cols-2 gap-2 w-full max-w-sm animate-fade-in opacity-0" style={{ animationDelay: '0.6s', animationFillMode: 'forwards' }}>
          <button onClick={() => onShop('sell')} className="p-3 bg-white/5 backdrop-blur-md rounded-xl border border-white/10 flex flex-col items-center gap-1">
            <Coins className="w-5 h-5 text-brand-primary" />
            <span className="text-[10px] font-bold uppercase tracking-wider">Sell Items</span>
          </button>
          <button onClick={() => onNavigate('donations')} className="p-3 bg-white/5 backdrop-blur-md rounded-xl border border-white/10 flex flex-col items-center gap-1">
            <HeartHandshake className="w-5 h-5 text-green-400" />
            <span className="text-[10px] font-bold uppercase tracking-wider">Donate Books</span>
          </button>
        </div>
      </div>
    </section>
  );
};

export default Hero;